'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  ShieldCheck, ArrowRight, Linkedin, Instagram, 
  Youtube, Phone, Mail, Menu, X, ChevronRight 
} from 'lucide-react';

export default function CookiesPolicyPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [currentYear, setCurrentYear] = useState('2026');

  // Garante que a data só será renderizada no lado do cliente,
  // evitando o erro de Hydration Mismatch
  useEffect(() => {
    setCurrentDate(new Date().toLocaleDateString('pt-BR'));
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const wppLink = "https://wa.me/554888796761?text=Quero%20falar%20com%20um%20especialista%20sobre%20o%20sistema%20Nexus%20Safe.";
  const appLink = "https://app.nexussafe.com.br/";

  return (
    <div className="min-h-screen bg-white selection:bg-blue-200 selection:text-blue-900 font-sans text-gray-800 flex flex-col relative overflow-x-hidden">
      
      {/* ================= NAVBAR ================= */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100 shadow-sm top-0">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between relative">
          <div className="flex items-center shrink-0 h-full">
            <Link href="/" className="flex items-center">
              <img 
                src="/logo-nexus.png" 
                alt="NexusSafe" 
                style={{ height: '40px', width: 'auto' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if(fallback) fallback.style.display = 'flex';
                }}
              />
              <div style={{ display: 'none' }} className="items-center gap-2">
                <ShieldCheck size={28} className="text-gray-900" />
                <span className="text-xl font-black text-gray-900 tracking-tight">NEXUS<span className="font-light">SAFE</span></span>
              </div>
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-4 lg:gap-8 font-bold text-sm text-gray-600">
            <Link href="/site/solucoes" className="hover:text-blue-600 transition-colors">Soluções</Link>
            <Link href="/site/para-quem-e" className="hover:text-blue-600 transition-colors">Para quem é</Link>
            <Link href="/site/recursos" className="hover:text-blue-600 transition-colors">Recursos</Link>
            <Link href="/site/conteudos" className="hover:text-blue-600 transition-colors">Conteúdos</Link>
            <Link href="/site/sobre-nos" className="hover:text-blue-600 transition-colors">Sobre nós</Link>
          </div>

          <div className="flex items-center gap-4 text-sm font-bold shrink-0">
            <div className="hidden md:flex items-center gap-4">
              <a href={appLink} className="text-gray-800 hover:text-blue-600 transition-colors border-r border-gray-300 pr-4">Entrar</a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20 whitespace-nowrap">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
            
            {/* Ícone Menu Mobile */}
            <button 
              className="md:hidden text-gray-800 p-2 transition-colors hover:text-blue-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Alternar menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* ================= MENU MOBILE (DROPDOWN) ================= */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white shadow-xl border-t border-gray-100 flex flex-col p-6 gap-4 z-40 max-h-[calc(100vh-80px)] overflow-y-auto">
            <Link href="/site/solucoes" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Soluções <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/para-quem-e" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Para quem é <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/recursos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Recursos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/conteudos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Conteúdos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/sobre-nos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Sobre nós <ChevronRight size={16} className="text-gray-400" />
            </Link>
            
            <div className="flex flex-col gap-3 mt-4">
              <a href={appLink} className="w-full text-center text-gray-900 font-bold py-3.5 border-2 border-gray-200 rounded-full hover:bg-gray-50 transition-colors">
                Entrar no Sistema
              </a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="w-full bg-blue-600 text-white py-3.5 rounded-full font-bold hover:bg-blue-700 transition-all flex justify-center items-center gap-2 shadow-lg shadow-blue-600/20">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ================= CONTEÚDO ================= */}
      <main className="flex-grow pt-32 pb-24 px-6 max-w-4xl mx-auto w-full">
        <div className="mb-10 border-b border-gray-100 pb-8 mt-10 md:mt-0">
          <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Política de Cookies</h1>
          <p className="text-gray-500 font-medium">Saiba como e por que utilizamos cookies em nosso sistema.</p>
          <p className="text-gray-500 font-medium mt-2">
            Última atualização: {currentDate ? currentDate : 'Carregando...'}
          </p>
        </div>

        <div className="space-y-8 text-gray-600 leading-relaxed font-medium">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. O que são Cookies?</h2>
            <p>
              Cookies são pequenos arquivos de texto que um site, ao ser visitado, coloca no computador do usuário ou no 
              seu dispositivo móvel através do navegador de internet (browser). Eles permitem que a plataforma memorize 
              suas ações e preferências (como login e idioma) por um determinado período de tempo.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Como a NexusSafe utiliza Cookies?</h2>
            <p className="mb-3">Empregamos cookies para três finalidades principais:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Cookies Estritamente Necessários:</strong> Essenciais para que a plataforma SaaS funcione corretamente. Permitem que você faça login com segurança e navegue em áreas restritas.</li>
              <li><strong>Cookies de Desempenho e Estatística:</strong> Ajudam-nos a entender como os visitantes interagem com o site, permitindo a otimização da navegação e identificação de erros de código.</li>
              <li><strong>Cookies de Marketing:</strong> Utilizados para rastrear a eficácia de nossas campanhas publicitárias corporativas. (Apenas no site público, não dentro da plataforma de clientes).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Gerenciamento e Aceite</h2>
            <p>
              Ao navegar pela primeira vez no site da NexusSafe, você será apresentado a um "Banner de Aceite" onde poderá 
              consentir com a utilização destas tecnologias. Caso deseje, você pode a qualquer momento desativar ou apagar os cookies 
              através das configurações do seu próprio navegador de internet. 
            </p>
            <p className="mt-3 text-red-500 text-sm">
              * Nota: A desativação de cookies "Estritamente Necessários" impedirá que você faça o login e utilize o painel de gestão do sistema.
            </p>
          </section>
        </div>
      </main>

      {/* ================= FOOTER ================= */}
      <footer style={{ backgroundColor: '#020617', paddingTop: '100px', paddingBottom: '40px' }} className="font-medium mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12 border-b border-gray-800 pb-16">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-6">
              <Link href="/" className="flex items-center">
                <img 
                  src="/logo-nexus-branca.png" 
                  alt="NexusSafe" 
                  style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if(fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{ display: 'none' }} className="items-center gap-2">
                  <ShieldCheck size={28} className="text-white" />
                  <span className="text-xl font-black text-white tracking-tight">NEXUSSAFE</span>
                </div>
              </Link>
            </div>
            <p style={{ color: '#9CA3AF' }} className="text-sm leading-relaxed mb-8">
              A plataforma inteligente que simplifica a gestão de Segurança e Saúde do Trabalho e conecta pessoas, dados e resultados.
            </p>
            <div className="flex gap-4">
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Linkedin size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Instagram size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Youtube size={18} /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Soluções</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Psicossocial + Plano</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Canal de Denúncia</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Checklist</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Plano de Ação</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Entrega de EPI</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Questionários</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">CIPA</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Para quem é</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Indústrias</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Construtoras</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Comércio</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Serviços</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Saúde</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Educação</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Recursos</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-8">
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Materiais</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Webinars</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Cases</Link></li>
            </ul>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Sobre Nós</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Institucional</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Parceiros</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Contato</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Fale Conosco</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-6">
              <li className="flex items-center gap-3"><Phone size={14} className="shrink-0"/> (48) 9.8879-6761</li>
              <li className="flex items-center gap-3"><Mail size={14} className="shrink-0"/> comercial@nexussafe.com.br</li>
            </ul>
            <a href={wppLink} target="_blank" rel="noopener noreferrer" className="inline-flex bg-blue-600 text-white px-5 py-2.5 rounded-full font-bold text-xs hover:bg-blue-700 transition-all items-center gap-2">
              Fale com um especialista <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <div style={{ color: '#9CA3AF' }} className="max-w-7xl mx-auto px-6 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <p className="text-center md:text-left">© {currentYear} NexusSafe - Todos os direitos reservados</p>
          <div className="flex items-center gap-6">
            <Link href="/site/privacidade" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Política de Privacidade</Link>
            <Link href="/site/termos" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Termos de Uso</Link>
            <Link href="/site/cookies" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Política de Cookies</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}